import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon } from '../common/icon';
import { Tracker } from '@/utils';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { isFromApp, isMobile } from '@/biz/tool';
import { s3ImageUrl } from '@/utils/config';
import DownloadAppStoreButtons, {
  buildBannerAttribution
} from '@/components/DownloadAppStoreButtons';

const DOWNLOAD_APP_BANNER_HIDE_KEY = 'download_app_banner_hide_until';
const SEVEN_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * 多层背景：background-image 中先写的层叠在上方。设计稿若把不透明白 linear 放在第一项会盖住全部蓝色。
 * 顺序（从顶到底）：底部白光晕 → 青径向 → 两路斜向蓝 → background-color 白底。
 * 勿用 Tailwind bg-[...]（逗号会被 JIT 拆开）。
 */
const DOWNLOAD_APP_BANNER_BG_IMAGE =
  'radial-gradient(63.34% 100% at 50% 100%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), radial-gradient(104.78% 136.14% at 34.23% 104.78%, rgba(147, 227, 255, 0) 0%, #D1F3FF 86.81%), linear-gradient(303.15deg, rgba(255, 255, 255, 0) 0%, rgba(42, 147, 255, 0.44) 91.85%), linear-gradient(291.54deg, #0B84FF 0%, rgba(255, 255, 255, 0) 100.5%)';

function shouldShowDownloadBanner() {
  if (typeof window === 'undefined') return false;
  if (isFromApp()) return false;
  try {
    const hideUntil = Number(
      localStorage.getItem(DOWNLOAD_APP_BANNER_HIDE_KEY)
    );
    if (hideUntil && !Number.isNaN(hideUntil) && Date.now() < hideUntil) {
      return false;
    }
  } catch (error) {
    // ignore storage errors
  }
  return true;
}

export default function DownloadAppBanner({
  className
}: {
  className?: string;
}) {
  const { t } = useTranslation();
  const hasReportedViewRef = useRef(false);
  const { user } = useUser();

  const [isShow, setIsShow] = useState(shouldShowDownloadBanner);

  const handleClose = (e: React.MouseEvent<HTMLDivElement>) => {
    Tracker.click('download_app_banner_close', 1, 'download_app_banner');
    e.stopPropagation();
    e.preventDefault();
    try {
      localStorage.setItem(
        DOWNLOAD_APP_BANNER_HIDE_KEY,
        String(Date.now() + SEVEN_DAYS_MS)
      );
    } catch (error) {
      // ignore storage errors and still hide for current session
    }
    setIsShow(false);
  };

  useEffect(() => {
    if (!isShow || hasReportedViewRef.current) return;
    hasReportedViewRef.current = true;
    Tracker.gtag('event', 'screen_view', {
      event_category: 'download_app_banner_view',
      utm_source: 'download_app_banner',
      utm_medium: 'app_banner',
      value: isMobile() ? 1 : 2
    });
  }, [isShow]);

  if (!isShow) return null;
  return (
    <div
      style={{
        backgroundColor: '#FFFFFF',
        backgroundImage: DOWNLOAD_APP_BANNER_BG_IMAGE
      }}
      className={`relative w-full p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4 justify-between overflow-hidden cursor-pointer ${className}`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex gap-[10px] md:gap-4 items-start">
          <img
            src={`${s3ImageUrl}phone.png`}
            alt=""
            className="w-8 md:w-[52px] flex-shrink-0 h-auto"
          />
          <div className="flex flex-col gap-1">
            <div className="w-full justify-start text-[#1C1917] text-lg font-semibold font-['DM_Sans'] leading-7">
              {t('downloadAppBanner.title', {
                defaultValue: 'Never miss a collab again.'
              })}
            </div>
            <div className="text-[#57534E] text-base font-normal font-['DM_Sans'] leading-6 hidden md:block">
              {t('downloadAppBanner.subtitle', {
                defaultValue:
                  'Instant alerts for new opportunities, progress updates, and payouts —\nin your pocket.'
              })}
            </div>
          </div>
        </div>
        <div className="text-[#57534E] text-base font-normal font-['DM_Sans'] leading-6 md:hidden">
          {t('downloadAppBanner.subtitle', {
            defaultValue:
              'Instant alerts for new opportunities, progress updates, and payouts —\nin your pocket.'
          })}
        </div>
      </div>
      <div
        className="flex items-center gap-4 mt-4 md:pr-[52px]"
        onClick={(e) => e.stopPropagation()}
      >
        <DownloadAppStoreButtons
          getAttribution={() => buildBannerAttribution(user?.id)}
          className="flex items-center gap-4"
          androidImgClassName="h-[40px] order-1 md:order-2 cursor-pointer"
          iosImgClassName="h-[40px] order-2 md:order-1 cursor-pointer"
          eventCategory="download_app_banner_click"
          openInNewTab
        />
      </div>
      <div
        className="w-[36px] h-[36px] flex items-center justify-center absolute top-2 right-2 cursor-pointer"
        onClick={handleClose}
      >
        <CloseIcon className="text-[#A9A29D] w-4 h-4" />
      </div>
    </div>
  );
}
